import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { formatApiError } from '../lib/api';
import { Check, ArrowRight, ArrowLeft, Mail, Search, RefreshCcw } from 'lucide-react';
import { Logo } from '../components/Logo';
import { PasswordStrength, isPasswordValid } from '../components/PasswordStrength';
import { PLANS, planQuote } from '../lib/plans';
import { BillingCyclePicker } from '../components/BillingCyclePicker';
import { PlanPriceDisplay } from '../components/PlanPriceDisplay';
import { useSiteSettings } from '../lib/useSiteSettings';

const ENTITY_OPTIONS = [
    { value: 'SRL', label: 'SRL (Societate cu Răspundere Limitată)' },
    { value: 'SA', label: 'SA (Societate pe Acțiuni)' },
    { value: 'PFA', label: 'PFA (Persoană Fizică Autorizată)' },
];

export default function Register() {
    const { registerStart, registerVerify, registerResendOtp, anafLookup } = useAuth();
    const { settings: site } = useSiteSettings();
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [pendingId, setPendingId] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Account
    const [account, setAccount] = useState({
        email: '', password: '', admin_name: '', phone: '', accept_terms: false,
    });
    // Company (billing)
    const [billing, setBilling] = useState({
        entity_type: 'SRL',
        cui: '', legal_name: '', trade_register: '',
        county: '', city: '', street: '', postal_code: '',
        bank_iban: '',
        billing_email: '', billing_phone: '',
    });
    const [anafLoading, setAnafLoading] = useState(false);
    const [anafMsg, setAnafMsg] = useState('');
    // Restaurant
    const [restaurant, setRestaurant] = useState({
        restaurant_name: '', plan: 'pro', billing_cycle: 'monthly',
    });
    // OTP
    const [otp, setOtp] = useState('');

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    const lookupCui = async () => {
        setError(''); setAnafMsg(''); setAnafLoading(true);
        try {
            const data = await anafLookup(billing.cui.trim());
            if (!data.is_active) {
                setAnafMsg('Atenție: firma e marcată INACTIVĂ în registrul ANAF.');
            } else {
                setAnafMsg('Date completate automat din registrul ANAF.');
            }
            const fullStreet = data.street + (data.street_no ? ' ' + data.street_no : '');
            setBilling(b => ({
                ...b,
                cui: data.cui,
                legal_name: data.legal_name || b.legal_name,
                trade_register: data.trade_register || b.trade_register,
                county: data.county || b.county,
                city: data.city || b.city,
                street: fullStreet.trim() || b.street,
                postal_code: data.postal_code || b.postal_code,
            }));
        } catch (err) {
            setError(formatApiError(err));
        } finally { setAnafLoading(false); }
    };

    const startRegister = async () => {
        setError(''); setLoading(true);
        try {
            const payload = {
                restaurant_name: restaurant.restaurant_name,
                admin_name: account.admin_name,
                email: account.email,
                password: account.password,
                phone: account.phone || null,
                plan: restaurant.plan,
                billing_cycle: restaurant.billing_cycle,
                accept_terms: account.accept_terms,
                billing: {
                    ...billing,
                    billing_email: billing.billing_email || account.email,
                },
            };
            const data = await registerStart(payload);
            setPendingId(data.pending_id);
            setResendCooldown(60);
            if (data.dev_code) {
                // dev convenience: prefill the OTP so testing is fast
                setOtp(data.dev_code);
            }
            setStep(4);
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    const verifyOtp = async () => {
        setError(''); setLoading(true);
        try {
            await registerVerify(pendingId, otp.trim());
            nav('/r', { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    const resendOtp = async () => {
        setError('');
        try {
            const data = await registerResendOtp(pendingId);
            setResendCooldown(60);
            if (data.dev_code) setOtp(data.dev_code);
            setAnafMsg(''); // reuse for transient info
            setError(''); // clear errors
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    // Phone: strip spaces/dashes then require 9-15 digits (with optional + prefix).
    const phoneNormalized = (account.phone || '').replace(/[\s\-()]/g, '');
    const phoneValid = /^\+?\d{9,15}$/.test(phoneNormalized);
    const step1Valid = account.email && isPasswordValid(account.password) && account.admin_name && phoneValid && account.accept_terms;
    const step2Valid = billing.cui && billing.legal_name && billing.trade_register && billing.county && billing.city && billing.street && (billing.billing_email || account.email);
    const step3Valid = restaurant.restaurant_name && restaurant.plan;

    return (
        <div className="grain" style={{ minHeight: '100vh', padding: '2rem 1rem' }}>
            <div style={{ maxWidth: 680, margin: '0 auto' }} className="fade-in-up">
                <div style={{ marginBottom: '1.4rem' }}>
                    <Logo size="lg" />
                </div>

                <h1 className="headline" style={{ fontSize: '2.4rem', margin: 0 }}>Înregistrează-ți restaurantul.</h1>
                <p style={{ color: 'var(--text-muted)', marginBottom: '1.6rem' }}>{site.trial_days} zile gratuit. Verificăm email-ul ca să fim siguri că poți primi facturi.</p>

                {/* Stepper */}
                <div style={{ display: 'flex', gap: 12, marginBottom: '1.6rem' }} data-testid="register-stepper">
                    {[1, 2, 3, 4].map(n => (
                        <div key={n} style={{
                            flex: 1, height: 4, borderRadius: 99,
                            background: n <= step ? 'var(--amber)' : 'var(--border)',
                            transition: 'background 0.3s',
                        }} />
                    ))}
                </div>

                <div className="panel" style={{ padding: '1.6rem' }}>
                    {/* ============== PAS 1: Cont ============== */}
                    {step === 1 && (
                        <div>
                            <div className="kicker">Pas 1 din 4</div>
                            <h2 className="headline" style={{ fontSize: '1.6rem', margin: '0.4rem 0 1.2rem' }}>Date cont</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                                <div>
                                    <label className="label">Email *</label>
                                    <input className="input" type="email" data-testid="reg-email"
                                           value={account.email}
                                           onChange={e => setAccount({ ...account, email: e.target.value })}
                                           placeholder="nume@domeniu.ro" />
                                    <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: 4 }}>
                                        Trimitem un cod de verificare pe această adresă.
                                    </div>
                                </div>
                                <div>
                                    <label className="label">Parolă *</label>
                                    <input className="input" type="password" data-testid="reg-password"
                                           value={account.password}
                                           onChange={e => setAccount({ ...account, password: e.target.value })}
                                           placeholder="••••••••" />
                                    <PasswordStrength value={account.password} />
                                </div>
                                <div>
                                    <label className="label">Numele tău *</label>
                                    <input className="input" data-testid="reg-admin-name"
                                           value={account.admin_name}
                                           onChange={e => setAccount({ ...account, admin_name: e.target.value })}
                                           placeholder="Nume Prenume" />
                                </div>
                                <div>
                                    <label className="label">Telefon *</label>
                                    <input className="input" type="tel" data-testid="reg-phone"
                                           value={account.phone}
                                           onChange={e => setAccount({ ...account, phone: e.target.value })}
                                           placeholder="07xx xxx xxx" />
                                    <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: 4 }}>
                                        Folosit pentru contact urgent legat de cont.
                                    </div>
                                </div>
                                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', marginTop: 4 }}>
                                    <input type="checkbox" checked={account.accept_terms}
                                           onChange={e => setAccount({ ...account, accept_terms: e.target.checked })}
                                           data-testid="reg-accept-terms" style={{ marginTop: 3 }} />
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                        Sunt de acord cu <Link to="/legal/terms" style={{ color: 'var(--amber)' }}>Termenii și Condițiile</Link> și prelucrarea datelor pentru facturare.
                                    </span>
                                </label>
                            </div>
                            <button className="btn btn-primary" style={{ width: '100%', marginTop: '1.4rem' }}
                                    disabled={!step1Valid}
                                    onClick={() => setStep(2)} data-testid="reg-step1-next">
                                Continuă <ArrowRight size={16} />
                            </button>
                        </div>
                    )}

                    {/* ============== PAS 2: Date firmă ============== */}
                    {step === 2 && (
                        <div>
                            <div className="kicker">Pas 2 din 4</div>
                            <h2 className="headline" style={{ fontSize: '1.6rem', margin: '0.4rem 0 1.2rem' }}>Date firmă (pentru facturare)</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                                <div>
                                    <label className="label">Tip entitate *</label>
                                    <select className="input" value={billing.entity_type}
                                            onChange={e => setBilling({ ...billing, entity_type: e.target.value })}
                                            data-testid="reg-entity-type">
                                        {ENTITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="label">CUI / CIF *</label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input className="input" data-testid="reg-cui" style={{ flex: 1 }}
                                               value={billing.cui}
                                               onChange={e => setBilling({ ...billing, cui: e.target.value })}
                                               placeholder="12345678 sau RO12345678" />
                                        <button type="button" className="btn btn-secondary"
                                                disabled={!billing.cui || anafLoading}
                                                onClick={lookupCui} data-testid="reg-anaf-lookup">
                                            {anafLoading ? <span className="spinner" /> : <><Search size={14} /> Caută ANAF</>}
                                        </button>
                                    </div>
                                    {anafMsg && (
                                        <div style={{ fontSize: '0.8rem', color: anafMsg.startsWith('Atenție') ? 'var(--neon-magenta)' : 'var(--neon-teal)', marginTop: 6 }}
                                             data-testid="reg-anaf-msg">{anafMsg}</div>
                                    )}
                                </div>
                                <div>
                                    <label className="label">Denumire legală *</label>
                                    <input className="input" data-testid="reg-legal-name"
                                           value={billing.legal_name}
                                           onChange={e => setBilling({ ...billing, legal_name: e.target.value })}
                                           placeholder='ex: "ABC RESTAURANT S.R.L."' />
                                </div>
                                <div>
                                    <label className="label">Nr. Registru Comerțului *</label>
                                    <input className="input" data-testid="reg-trade-register"
                                           value={billing.trade_register}
                                           onChange={e => setBilling({ ...billing, trade_register: e.target.value })}
                                           placeholder="J40/123/2020 sau F40/.../..." />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' }}>
                                    <div>
                                        <label className="label">Județ *</label>
                                        <input className="input" data-testid="reg-county"
                                               value={billing.county}
                                               onChange={e => setBilling({ ...billing, county: e.target.value })}
                                               placeholder="București" />
                                    </div>
                                    <div>
                                        <label className="label">Oraș *</label>
                                        <input className="input" data-testid="reg-city"
                                               value={billing.city}
                                               onChange={e => setBilling({ ...billing, city: e.target.value })}
                                               placeholder="Sector 3" />
                                    </div>
                                </div>
                                <div>
                                    <label className="label">Stradă, nr. *</label>
                                    <input className="input" data-testid="reg-street"
                                           value={billing.street}
                                           onChange={e => setBilling({ ...billing, street: e.target.value })}
                                           placeholder="Str. Exemplu nr. 12" />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' }}>
                                    <div>
                                        <label className="label">Cod poștal</label>
                                        <input className="input" data-testid="reg-postal"
                                               value={billing.postal_code}
                                               onChange={e => setBilling({ ...billing, postal_code: e.target.value })}
                                               placeholder="030123" />
                                    </div>
                                    <div>
                                        <label className="label">IBAN (opțional)</label>
                                        <input className="input" data-testid="reg-iban"
                                               value={billing.bank_iban}
                                               onChange={e => setBilling({ ...billing, bank_iban: e.target.value })}
                                               placeholder="RO49 AAAA 1B31..." />
                                    </div>
                                </div>
                                <div>
                                    <label className="label">Email facturi (opțional — implicit cel de cont)</label>
                                    <input className="input" type="email" data-testid="reg-billing-email"
                                           value={billing.billing_email}
                                           onChange={e => setBilling({ ...billing, billing_email: e.target.value })}
                                           placeholder={account.email || 'facturi@firma.ro'} />
                                </div>
                            </div>
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginTop: '0.9rem' }} data-testid="reg-error">{error}</div>}
                            <div style={{ display: 'flex', gap: 10, marginTop: '1.4rem' }}>
                                <button className="btn btn-secondary" onClick={() => setStep(1)} data-testid="reg-step2-back"><ArrowLeft size={16} /> Înapoi</button>
                                <button className="btn btn-primary" style={{ flex: 1 }} disabled={!step2Valid}
                                        onClick={() => setStep(3)} data-testid="reg-step2-next">
                                    Continuă <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ============== PAS 3: Restaurant + plan ============== */}
                    {step === 3 && (
                        <div>
                            <div className="kicker">Pas 3 din 4</div>
                            <h2 className="headline" style={{ fontSize: '1.6rem', margin: '0.4rem 0 1.2rem' }}>Restaurant și plan</h2>
                            <div style={{ marginBottom: '1rem' }}>
                                <label className="label">Nume restaurant *</label>
                                <input className="input" data-testid="reg-restaurant-name"
                                       value={restaurant.restaurant_name}
                                       onChange={e => setRestaurant({ ...restaurant, restaurant_name: e.target.value })}
                                       placeholder="Bistro Vintage" />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <BillingCyclePicker
                                    value={restaurant.billing_cycle}
                                    onChange={(id) => setRestaurant({ ...restaurant, billing_cycle: id })}
                                    testId="reg-billing-cycle"
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                                {PLANS.map(p => {
                                    const q = planQuote(p.id, restaurant.billing_cycle);
                                    return (
                                        <button key={p.id} onClick={() => setRestaurant({ ...restaurant, plan: p.id })}
                                                data-testid={`reg-plan-${p.id}`}
                                                style={{
                                                    background: restaurant.plan === p.id ? 'rgba(15, 101, 112, 0.12)' : 'var(--surface-2)',
                                                    border: `1px solid ${restaurant.plan === p.id ? 'var(--brand-teal-light)' : 'var(--border)'}`,
                                                    borderRadius: '0.75rem', padding: '1rem', cursor: 'pointer',
                                                    textAlign: 'left', color: '#fff', transition: 'all 0.2s',
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                                                }}>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{p.name}</div>
                                                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 2 }}>
                                                    {p.perks.slice(0, 3).join(' • ')}
                                                </div>
                                                {q.showOriginalPrice && (
                                                    <span className="badge badge-magenta" style={{ marginTop: 6, fontSize: '0.62rem' }}>−{q.savingsPercent}% reducere</span>
                                                )}
                                            </div>
                                            <div style={{ textAlign: 'right', minWidth: 140 }}>
                                                <PlanPriceDisplay plan={p} cycleId={restaurant.billing_cycle} size="sm" variant="card" />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginTop: '0.9rem' }} data-testid="reg-error">{error}</div>}
                            <div style={{ display: 'flex', gap: 10, marginTop: '1.4rem' }}>
                                <button className="btn btn-secondary" onClick={() => setStep(2)} data-testid="reg-step3-back"><ArrowLeft size={16} /> Înapoi</button>
                                <button className="btn btn-primary" style={{ flex: 1 }}
                                        disabled={!step3Valid || loading}
                                        onClick={startRegister} data-testid="reg-step3-send">
                                    {loading ? <span className="spinner" /> : <>Trimite codul pe email <Mail size={16} /></>}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ============== PAS 4: Verificare OTP ============== */}
                    {step === 4 && (
                        <div>
                            <div className="kicker">Pas 4 din 4</div>
                            <h2 className="headline" style={{ fontSize: '1.6rem', margin: '0.4rem 0 0.8rem' }}>Verificare email</h2>
                            <p style={{ color: 'var(--text-muted)', marginBottom: '1.2rem', fontSize: '0.92rem' }}>
                                Am trimis un cod de 6 cifre pe <strong>{account.email}</strong>. Introdu-l mai jos pentru a finaliza înregistrarea.
                            </p>
                            <label className="label">Cod verificare</label>
                            <input className="input" data-testid="reg-otp"
                                   value={otp}
                                   onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                                   placeholder="123456"
                                   inputMode="numeric"
                                   maxLength={6}
                                   style={{ fontSize: '1.6rem', letterSpacing: 6, textAlign: 'center', fontFamily: 'monospace' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                <span style={{ fontSize: '0.82rem', color: 'var(--text-faint)' }}>Codul e valabil 15 minute.</span>
                                <button type="button" className="btn btn-ghost btn-sm" disabled={resendCooldown > 0} onClick={resendOtp} data-testid="reg-resend">
                                    <RefreshCcw size={12} /> {resendCooldown > 0 ? `Retrimite (${resendCooldown}s)` : 'Retrimite codul'}
                                </button>
                            </div>
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginTop: '0.9rem' }} data-testid="reg-error">{error}</div>}
                            <div style={{ display: 'flex', gap: 10, marginTop: '1.4rem' }}>
                                <button className="btn btn-secondary" onClick={() => { setStep(3); setError(''); }} data-testid="reg-step4-back"><ArrowLeft size={16} /> Înapoi</button>
                                <button className="btn btn-primary" style={{ flex: 1 }}
                                        disabled={otp.length < 4 || loading}
                                        onClick={verifyOtp} data-testid="reg-verify">
                                    {loading ? <span className="spinner" /> : <>Finalizează înregistrarea <Check size={16} /></>}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ textAlign: 'center', marginTop: '1.4rem', color: 'var(--text-muted)' }}>
                    Ai deja cont? <Link to="/login" style={{ color: 'var(--amber)' }} data-testid="goto-login">Autentifică-te</Link>
                </div>
            </div>
        </div>
    );
}
